import classNames from 'classnames';
import React from 'react';

import {assertAndContinue} from 'Asserts';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {
  getIconIdForSearchResult,
  getTooltipTitleForSearchResult,
  ItemOption,
  ItemOptionTitle,
  SearchResultOption,
} from 'ui-library/inputs/ItemOptionContent';
import {OptionData} from 'ui-library/menus/MenuConstants';

import {AttachDropdownItem} from './AttachDropdownConstants';

interface AttachDropdownOptionProps {
  result: AttachDropdownItem;
}
function AttachDropdownOption({result}: AttachDropdownOptionProps): JSX.Element {
  return (
    <Tooltip
      placement={FloatingPlacement.TOP}
      text={getTooltipTitleForSearchResult(result)}
      disableContentSelection={true}
    >
      <div>
        <BenchlingIcon className="attachDropdown-option-icon" icon={getIconIdForSearchResult(result)} />
        <div className="attachDropdown-option-info">
          <SearchResultOption searchResult={result} />
        </div>
      </div>
    </Tooltip>
  );
}

/**
 * Quick note: the entire AttachDropdown system is generally old and superseded by Dropdown/Menu.
 * The component here is used as a bridge: an OptionData is used by the newer Menu component,
 * but `CustomAttachDropdownFromOptionData` allows you to render an OptionData inside an older
 * AttachDropdown.
 *
 * It is not meant to be complete. I.e., it does not respond to all available properties of the
 * OptionData structure.
 */
export function CustomAttachDropdownFromOptionData({optionData}: {optionData: OptionData}): JSX.Element {
  const {name, icon, className, iconClassName, dataTestAttrs, ...otherProps} = optionData;
  assertAndContinue(
    Object.keys(otherProps).length === 0,
    'CustomAttachDropdownFromOptionData does not support prop(s)',
    {unsupported: otherProps}
  );
  return (
    <CustomAttachDropdownOption
      title={optionData.name}
      icon={optionData.icon}
      iconClassName={optionData.iconClassName}
      className={optionData.className}
    />
  );
}

interface CustomAttachDropdownOptionProps {
  title: string | JSX.Element;
  tooltip?: string;
  icon?: BenchlingIconName;
  iconClassName?: string;
  className?: string;
  /**
   * Renders as a faded subtitle.
   */
  metadata?: React.ReactNode;
}
// Output is the same as above. This version lets you specify the texts directly, rather than having
// them interpreted from a searchResult. Only difference is that this will only render a one-line option.
export function CustomAttachDropdownOption({
  title,
  metadata,
  tooltip,
  icon,
  iconClassName,
  className,
}: CustomAttachDropdownOptionProps): JSX.Element {
  return (
    <Tooltip placement={FloatingPlacement.TOP} text={tooltip}>
      <div className={className}>
        {icon ? (
          <BenchlingIcon className={classNames('attachDropdown-option-icon', iconClassName)} icon={icon} />
        ) : null}
        <div className={icon ? 'attachDropdown-option-info' : 'attachDropdown-option-text'}>
          <ItemOption title={<ItemOptionTitle title={title} />} metadata={metadata} />
        </div>
      </div>
    </Tooltip>
  );
}

export default AttachDropdownOption;
